import CloseIcon from '@mui/icons-material/Close';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface DrawerHeaderProps {
  onCancel: () => void;
  /** Header title — defaults to the column-configurator title. */
  title?: string;
  /** Inner header icon element — defaults to the column icon. */
  icon?: ReactNode;
  /** Close-button tooltip — defaults to the column-configurator cancel label. */
  closeTooltip?: string;
  /** id set on the title node so the drawer can reference it via aria-labelledby. */
  titleId?: string;
}

export function DrawerHeader({ onCancel, title, icon, closeTooltip, titleId }: DrawerHeaderProps) {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const { main: primary, light: primaryLight, dark: primaryDark } = theme.palette.primary;
  const resolvedTitle = title ?? t('columnConfigurator.title');
  const resolvedIcon = icon ?? <ViewColumnIcon sx={{ fontSize: 'var(--icon-header)', color: 'white' }} />;
  const resolvedCloseTooltip = closeTooltip ?? t('columnConfigurator.cancel');

  return (
    <Box
      sx={{
        background: `linear-gradient(135deg, ${primaryLight} 0%, ${primary} 60%, ${primaryDark} 100%)`,
        px: 2.5,
        pt: 2.5,
        pb: 0,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        flexShrink: 0,
        borderTopLeftRadius: 'var(--border-radius)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 2 }}>
        <Box
          sx={{
            width: 'var(--header-icon-size)',
            height: 'var(--header-icon-size)',
            borderRadius: 'var(--header-icon-radius)',
            bgcolor: 'var(--white-15)',
            backdropFilter: 'blur(8px)',
            border: '1px solid var(--white-25)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            boxShadow: 'inset 0 1px 1px var(--white-20)',
          }}
        >
          {resolvedIcon}
        </Box>
        <Typography
          id={titleId}
          sx={{
            color: 'white',
            fontWeight: theme.typography.fontWeightBold,
            fontSize: 'var(--font-title)',
            lineHeight: 1.15,
            letterSpacing: '-0.02em',
          }}
        >
          {resolvedTitle}
        </Typography>
      </Box>

      <Tooltip title={resolvedCloseTooltip} placement="left" disableInteractive>
        <IconButton
          onClick={onCancel}
          sx={{
            width: 'var(--close-button-size)',
            height: 'var(--close-button-size)',
            color: 'var(--white-80)',
            mt: 0.25,
            '&:hover': { bgcolor: 'var(--white-15)', color: 'white' },
          }}
        >
          <CloseIcon sx={{ fontSize: 'var(--icon-close)' }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}
